import config from '@/config';
import { defineStore } from 'pinia';
import * as platformClient from 'purecloud-platform-client-v2';
import type { Models } from 'purecloud-platform-client-v2';
import { USER_DISPLAY_NAMES } from '@/constants';
import { isMockMode, getMockParams } from '@/dev/mockMode';

const client = platformClient.ApiClient.instance;
client.setEnvironment(config.genesysCloud.region);
client.setPersistSettings(true, 'genesys-cuca-ai-app');
const usersApi = new platformClient.UsersApi();

/**
 * Normalize an agent language value against the configured supported languages.
 * Falls back to the default language when the value is missing, empty, or unsupported.
 * This prevents typos and path-injection via the `agentLang` URL param / OAuth state.
 */
function normalizeAgentLang(value: string | null | undefined): string {
  const supported = config.localization.supportedLanguages;
  const fallback = config.localization.defaultLanguage;
  if (!value) return fallback;
  // Direct match first (e.g. 'nl', 'de')
  if (supported.includes(value)) return value;
  // Also accept BCP-47 tags (e.g. 'nl-NL', 'de-DE') by matching the base language code
  const base = value.split('-')[0].toLowerCase();
  return supported.find((s) => s.toLowerCase() === base) ?? fallback;
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isLoggedIn: false,
    agent: null as Models.UserMe | null,
    userId: null as string | null,
    isInitializing: false,
    error: null as string | null,
    conversationId: null as string | null,
    language: null as string | null,
    agentLang: 'nl',
    // Dealer/shop associated with the conversation, read from Genesys
    // participant attributes during history load (may be absent).
    dealerNumber: null as string | null,
  }),

  getters: {
    isAuthenticated: (state) => state.isLoggedIn && state.agent !== null,
    agentName: (state) => state.agent?.name || USER_DISPLAY_NAMES.AGENT,
  },

  actions: {
    /**
     * Initialize app and handle authentication via SDK.
     */
    async initialize() {
      this.isInitializing = true;
      this.error = null;

      // Dev-only: bypass Genesys OAuth entirely and use fake identity/params.
      if (isMockMode()) {
        this._initializeMock();
        this.isInitializing = false;
        return;
      }

      try {
        const clientId = config.genesysCloud.clientId;
        if (!clientId) throw new Error('Client ID not found in configuration.');

        const cleanRedirectUri = window.location.origin;
        const urlParams = new URLSearchParams(window.location.search);
        const initialCId = urlParams.get('cId');
        const initialLang = urlParams.get('lang');
        const initialAgentLang = normalizeAgentLang(urlParams.get('agentLang'));

        // SDK handles token persistence and redirects automatically.
        // Pass initial params as 'state' to survive redirect.
        const encodedState =
          initialCId && initialLang
            ? btoa(
                JSON.stringify({
                  cId: initialCId,
                  lang: initialLang,
                  agentLang: initialAgentLang,
                })
              )
            : undefined;

        const authData = await client.loginImplicitGrant(
          clientId,
          cleanRedirectUri,
          { state: encodedState }
        );

        // Determine conversation parameters source.
        // Priority 1: OAuth redirect state
        if (authData?.state) {
          this._processOAuthState(authData.state);
          if (this.error) {
            throw new Error(this.error);
          }
        }
        // Priority 2: Initial URL parameters
        else if (initialCId && initialLang) {
          this.conversationId = initialCId;
          this.language = initialLang;
          this.agentLang = initialAgentLang;
        }

        // Validate required parameters after processing
        // (agentLang is NOT required — defaults to 'nl')
        if (!this.conversationId || !this.language) {
          throw new Error(
            'Missing required parameters: cId and lang are required after authentication.'
          );
        }

        // Authentication successful
        this.isLoggedIn = true;
        this.agent = await usersApi.getUsersMe();
        this.userId = this.agent.id || null;
      } catch (err) {
        this.error =
          err instanceof Error ? err.message : 'Authentication failed';
        console.error('Authentication failed:', err);
      } finally {
        this.isInitializing = false;
      }
    },

    /**
     * Process OAuth redirect state parameter.
     */
    _processOAuthState(returnedState: string) {
      let decoded: string;
      try {
        decoded = atob(returnedState);
      } catch (e) {
        console.error('Failed to decode base64 state from SDK response:', e);
        this.error = 'Failed to decode authentication state (invalid base64).';
        return;
      }

      let decodedState: { cId?: string; lang?: string; agentLang?: string };
      try {
        decodedState = JSON.parse(decoded);
      } catch (e) {
        console.error('Failed to parse JSON from decoded state:', e);
        this.error = 'Failed to parse authentication state (invalid JSON).';
        return;
      }

      this.conversationId = decodedState.cId || null;
      this.language = decodedState.lang || null;
      this.agentLang = normalizeAgentLang(decodedState.agentLang);

      // Clean URL by removing auth fragments
      window.history.replaceState({}, document.title, window.location.pathname);
    },

    /**
     * Store the dealer number resolved from Genesys participant attributes.
     */
    setDealerNumber(dealerNumber: string | null) {
      this.dealerNumber = dealerNumber || null;
    },

    /**
     * Dev-only mock login: fake the agent identity and read the conversation
     * parameters from the URL (`cId`/`lang`) with sensible defaults.
     */
    _initializeMock() {
      const mock = getMockParams();
      this.conversationId = mock.conversationId;
      this.language = mock.language;
      this.agentLang = normalizeAgentLang(mock.agentLanguage);
      this.userId = 'mock-agent-001';
      this.agent = {
        id: 'mock-agent-001',
        name: mock.agentName,
      } as Models.UserMe;
      this.isLoggedIn = true;
      console.warn('[mock] Genesys mock mode active - OAuth bypassed');
    },
  },
});
